import type { AppEnvironment } from './types';
import { IP4CMS_PAGE_COPY } from './page-copy-ip4cms';

/**
 * Stellenbosch Municipality member portal — STAGING, single-tenant (NOT SSO).
 *
 * Clone of `sbmdc-staging` pinned to the `stellenboschmuni` tenant
 * (ece28301-a19e-41c2-865b-fbeac44287cc) instead of SBM. Same ip4cms-staging
 * backend (core-api / api .ip4cms-staging.ip4x.systems). Served from
 * `dist/stellmunidc-staging` behind Caddy at
 * `stellmunidc.member.ip4cms-staging.ip4x.systems`.
 *
 * The subdomain `stellmunidc` does NOT match the tenant slug `stellenboschmuni`,
 * so boot resolution falls through to core's custom-domain lookup
 * (tenantdomain.domain → ece28301…). That `tenantdomain` row + the host in
 * core's CADDY_TLS_ALLOW_HOSTS are required for resolve-domain + on-demand TLS.
 *
 * Branding currently reuses SBM's PWA icons/theme as a placeholder (per the
 * clone decision) — swap pwa-assets/stellmunidc-staging/* + theme once real
 * Stellenbosch assets exist.
 */
export const env: AppEnvironment = {
  production: true,
  buildMode: 'stellmunidc-staging',
  apiBaseUrl: 'https://api.ip4cms-staging.ip4x.systems/v1',
  coreApiUrl: 'https://core-api.ip4cms-staging.ip4x.systems/v1',
  // Shared "DCApp" member app template (code `dcapp`) owned by the ip4cms tenant —
  // same baseline template as sbmdc-staging (SBM + Stellenbosch share one DC app).
  appTemplateId: 'fbb54d26-d0e8-4f98-99cf-7ca377ab0a72',
  push: { enabled: true },
  // 'tenant' (NOT 'core'): standalone single-tenant app, authenticate directly
  // against the tenant API (/auth/login), no SSO session / community switcher.
  authMode: 'tenant',
  portalOwnerType: 'member',
  loadDomainConfig: true,
  // Single-tenant: defaultDomain pins the app to the stellenboschmuni tenant so
  // launch resolves its branding directly. Empty rootTenantId = no SSO scope.
  defaultDomain: 'stellmunidc.member.ip4cms-staging.ip4x.systems',
  rootTenantId: '',
  portalBaseUrl: 'https://stellmunidc.member.ip4cms-staging.ip4x.systems',
  appName: 'Stellenbosch Municipality',
  version: '1.0.0',
  appShell: 'classic',
  keyboardShift: true,
  welcomeContent: {
    eyebrow: 'Stellenbosch Municipality',
    title: 'Your community,\nin your pocket.',
    subtitle: 'Access, services and your home — all in one place.',
  },
  communitySelectContent: {
    title: 'Choose your community',
    subtitle: 'Search for the community you live in to begin registration.',
  },
  signupTermsUrl: 'https://www.glovent.co.za/terms',
  // Same "providers" supplier copy override as SBM (shared IP4CMS_PAGE_COPY base).
  pageCopy: {
    ...IP4CMS_PAGE_COPY,
    suppliers: {
      eyebrow: 'Local services',
      title: 'Providers.',
      subtitle: 'Browse approved providers by category.',
      emptyTitle: 'No providers.',
      emptyDescription: "Your community hasn't published any providers yet.",
    },
  },
  home: {
    widgetMode: true,
    widgets: [
      { id: 'account', type: 'account', title: 'Account', order: 1, enabled: true, requiresModule: 'financials' },
      { id: 'support', type: 'support', title: 'Helpdesk', order: 2, enabled: true, requiresModule: 'support' },
      { id: 'events', type: 'events', title: 'Upcoming events', order: 3, enabled: true, requiresModule: 'events' },
      { id: 'panic', type: 'panic', title: 'Emergency', order: 4, enabled: true, requiresModule: 'panic' },
      { id: 'utilities', type: 'utilities', title: 'Utilities', order: 5, enabled: true, requiresModule: 'utilities' },
      { id: 'access', type: 'access', title: 'Visitor access', order: 6, enabled: true, requiresModule: 'access_management' },
      { id: 'news', type: 'news', title: 'News', order: 7, enabled: true, requiresModule: 'newsstand' },
    ],
  },
};
